import { fail } from './errors.js';

/**
 * 할 일 관리(GTD): 사람마다 따로 갖는 개인 업무 목록. 치료 과제(tasks.ts)와는 별개다.
 * 흐름: 수집(INBOX) → 명료화·정리(ACTIVE/WAITING/SOMEDAY, 프로젝트) → 실행(오늘·다음 행동) → 주간 검토.
 * 저장할 때는 내용 전체를 암호화한다(services/api/src/routes/todos.ts). 여기는 평문 기준의 규칙만 둔다.
 */
export const TODO_BUCKETS = ['INBOX', 'ACTIVE', 'WAITING', 'SOMEDAY'] as const;
export type TodoBucket = (typeof TODO_BUCKETS)[number];
export const TODO_STATUSES = ['OPEN', 'DONE', 'CANCELED'] as const;
export type TodoStatus = (typeof TODO_STATUSES)[number];
export const TODO_VIEWS = ['INBOX', 'TODAY', 'UPCOMING', 'NEXT', 'WAITING', 'SOMEDAY', 'LOGBOOK'] as const;
export type TodoView = (typeof TODO_VIEWS)[number];
export const TODO_PROJECT_STATUSES = ['OPEN', 'SOMEDAY', 'DONE', 'CANCELED'] as const;
export type TodoProjectStatus = (typeof TODO_PROJECT_STATUSES)[number];

/** 한 사람이 가질 수 있는 항목 수(목록을 읽을 때마다 전부 복호화하므로 상한을 둔다). */
export const MAX_TODO_RECORDS = 3000;
/**
 * 목록을 읽을 때 보내는 끝난 할 일 수. 열린 프로젝트의 끝난 일(진행률 계산용)은 이 수와 별개로 모두 보낸다.
 * 응답이 Lambda 응답 크기(6MB)를 넘지 않게 하려는 것이다.
 */
/** 맡긴 사람이 취소한 사본에 남기는 제목(원래 내용은 지운다). */
export const CANCELED_BY_DELEGATOR_TITLE = '원장이 취소한 일';
export const TODO_LOGBOOK_LIMIT = 300;
/** 주간 검토 주기(일)와, 검토에서 "오래 기다리는 중"으로 볼 기준(일). */
export const TODO_REVIEW_DAYS = 7;
export const TODO_WAITING_STALE_DAYS = 14;

export interface TodoChecklistItem { id: string; text: string; done: boolean }

export interface Todo {
  id: string;
  ownerId: string;
  bucket: TodoBucket;
  status: TodoStatus;
  title: string;
  notes: string;
  checklist: TodoChecklistItem[];
  tags: string[];
  /** 언제 할지(이 날짜부터 오늘 목록에 나온다). 마감(deadline)과 다르다. */
  startDate?: string;
  evening: boolean;
  deadline?: string;
  projectId?: string;
  headingId?: string;
  /** 기다리는 대상(사람·기관). WAITING 에서만 쓴다. */
  waitingFor?: string;
  /** 원장이 맡긴 일의 원본: 받는 사람의 사본을 가리킨다. 사본 내용은 볼 수 없고 끝났는지만 받는다. */
  delegatedTo?: { userId: string; name: string; todoId: string; doneAt?: string };
  /** 맡겨 받은 사본: 맡긴 사람의 원본을 가리킨다. */
  delegatedFrom?: { userId: string; name: string; todoId: string };
  /** 맡길 때 연결한 아동(센터 이용자 id). 있으면 그 아동의 담당 선생님에게만 맡길 수 있다. */
  clientId?: string;
  /** 맡긴 사람이 취소해 내용을 비운 사본("원장이 취소함"). */
  canceledByDelegator?: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  version: number;
}

export interface TodoProject {
  id: string;
  ownerId: string;
  title: string;
  /** 프로젝트 목표(무엇이 되면 끝인가) */
  goal: string;
  /** 영역(예: 센터 운영, 가족). 왼쪽 목록에서 프로젝트를 묶는다. */
  area: string;
  headings: Array<{ id: string; title: string }>;
  status: TodoProjectStatus;
  deadline?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  version: number;
}

export interface TodoReview { lastReviewedAt: string }

/** 저장 형태. 목록·날짜까지 내용 전체가 payloadEnc 안에 있다. */
export interface TodoRecord {
  ownerId: string;
  id: string;
  kind: 'TODO' | 'PROJECT' | 'REVIEW';
  version: number;
  updatedAt: string;
  payloadEnc: string;
}
/** 주간 검토 기록은 사람마다 하나다. */
export const TODO_REVIEW_ID = 'review';

export type TodoPatch = Partial<{
  title: string; notes: string; checklist: TodoChecklistItem[]; tags: string[]; bucket: TodoBucket;
  startDate: string | null; evening: boolean; deadline: string | null; projectId: string | null; headingId: string | null; waitingFor: string | null;
}>;
export type TodoProjectPatch = Partial<{ title: string; goal: string; area: string; headings: TodoProject['headings']; deadline: string | null }>;

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const utc = (date: string) => Date.parse(`${date}T00:00:00Z`);
/** 2026-13-01, 2026-02-30 같은 없는 날짜는 거른다(없는 날짜로 toISOString 을 부르면 예외가 난다). */
const realDate = (s: string) => {
  if (!DATE.test(s)) return false;
  const ms = utc(s);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === s;
};
function checkDate(v: string | undefined, name: string) {
  if (v !== undefined && !realDate(v)) fail('VALIDATION', `${name} 날짜가 올바르지 않습니다.`);
}
const cleanTags = (tags: readonly string[]) => [...new Set(tags.map((t) => t.trim().replace(/^#/, '')).filter(Boolean))];

function checkTitle(title: string, name: string, max = 200) {
  const t = title.trim();
  if (!t || t.length > max) fail('VALIDATION', `${name}은(는) 1~${max}자입니다.`);
  return t;
}

function checkVersion(cur: { version: number }, expected: number) {
  if (cur.version !== expected) fail('CONFLICT', '다른 화면에서 먼저 변경되었습니다.');
}

/** 어떤 경로로 고쳐도 같은 규칙이 지켜지도록 저장 직전에 항상 거친다. */
function normalized(t: Todo): Todo {
  const title = checkTitle(t.title, '할 일');
  if (t.notes.length > 4000) fail('VALIDATION', '메모는 4000자까지입니다.');
  if (t.checklist.length > 50) fail('VALIDATION', '체크리스트는 50개까지입니다.');
  const checklist = t.checklist.map((c) => ({ id: c.id, text: checkTitle(c.text, '체크리스트 항목'), done: c.done }));
  const tags = cleanTags(t.tags);
  if (tags.length > 10 || tags.some((x) => x.length > 20)) fail('VALIDATION', '태그는 10개까지, 하나에 20자까지입니다.');
  checkDate(t.startDate, '시작');
  checkDate(t.deadline, '마감');
  const waitingFor = t.bucket === 'WAITING' ? t.waitingFor?.trim() || undefined : undefined;
  if (t.bucket === 'WAITING' && !waitingFor) fail('VALIDATION', '누구를 기다리는지 적어 주세요.');
  if (waitingFor && waitingFor.length > 80) fail('VALIDATION', '기다리는 대상은 80자까지입니다.');
  if (t.delegatedTo && t.status === 'OPEN' && t.bucket !== 'WAITING') fail('CONFLICT', '맡긴 일은 기다리는 중에서만 관리합니다. 끝내려면 완료하거나 취소하세요.');
  const noDate = t.bucket === 'INBOX' || t.bucket === 'SOMEDAY';
  const startDate = noDate ? undefined : t.startDate;
  return {
    ...t, title, checklist, tags, waitingFor, startDate,
    evening: t.bucket === 'ACTIVE' && Boolean(startDate) && t.evening,
    projectId: t.bucket === 'INBOX' ? undefined : t.projectId,
    headingId: t.bucket === 'INBOX' || !t.projectId ? undefined : t.headingId,
  };
}

export function newTodo(i: {
  id: string; ownerId: string; title: string; now: string; bucket?: TodoBucket; notes?: string; checklist?: TodoChecklistItem[]; tags?: string[];
  startDate?: string; evening?: boolean; deadline?: string; projectId?: string; headingId?: string; waitingFor?: string;
  delegatedFrom?: Todo['delegatedFrom'];
}): Todo {
  const bucket = i.bucket ?? (i.startDate || i.projectId ? 'ACTIVE' : 'INBOX');
  return normalized({
    id: i.id, ownerId: i.ownerId, bucket, status: 'OPEN', title: i.title, notes: i.notes ?? '', checklist: i.checklist ?? [], tags: i.tags ?? [],
    startDate: i.startDate, evening: i.evening ?? false, deadline: i.deadline, projectId: i.projectId, headingId: i.headingId, waitingFor: i.waitingFor,
    delegatedFrom: i.delegatedFrom, createdAt: i.now, updatedAt: i.now, version: 1,
  });
}

/** 내용·위치 수정. 맡기기 연결(delegated*)과 상태는 이 경로로 바꿀 수 없다. */
export function updateTodo(t: Todo, patch: TodoPatch, expectedVersion: number, now: string): Todo {
  checkVersion(t, expectedVersion);
  if (t.status !== 'OPEN') fail('CONFLICT', '끝난 할 일은 다시 연 뒤에 고칠 수 있습니다.');
  const { startDate, deadline, projectId, headingId, waitingFor, ...rest } = patch;
  const next: Todo = { ...t, ...rest, updatedAt: now, version: t.version + 1 };
  if (startDate !== undefined) next.startDate = startDate ?? undefined;
  if (deadline !== undefined) next.deadline = deadline ?? undefined;
  if (projectId !== undefined && projectId !== t.projectId) { next.projectId = projectId ?? undefined; next.headingId = undefined; }
  if (headingId !== undefined) next.headingId = headingId ?? undefined;
  if (waitingFor !== undefined) next.waitingFor = waitingFor ?? undefined;
  // 날짜나 프로젝트를 정하면 그것으로 수신함을 벗어난다(정리 완료)
  if (!patch.bucket && t.bucket === 'INBOX' && (next.startDate || next.projectId)) next.bucket = 'ACTIVE';
  return normalized(next);
}

export function setTodoStatus(t: Todo, status: TodoStatus, expectedVersion: number, now: string): Todo {
  checkVersion(t, expectedVersion);
  if (t.status === status) fail('CONFLICT', '이미 처리되었습니다.');
  if (t.delegatedFrom && status === 'CANCELED') fail('CONFLICT', '맡겨 받은 일은 취소할 수 없습니다. 맡긴 분께 말씀해 주세요.');
  if (t.delegatedFrom && t.status === 'CANCELED') fail('CONFLICT', '맡긴 분이 취소한 일은 다시 열 수 없습니다.');
  if (t.delegatedTo && status === 'OPEN') fail('CONFLICT', '맡겼다가 끝낸 일은 다시 열 수 없습니다. 새로 맡겨 주세요.');
  const { completedAt: _old, ...rest } = t;
  return { ...rest, status, updatedAt: now, version: t.version + 1, ...(status === 'OPEN' ? {} : { completedAt: now }) };
}

/** 받는 사람의 수신함에 사본을 넣고, 원본은 기다리는 중으로 옮긴다. */
export function delegateTodo(t: Todo, i: { copyId: string; from: { userId: string; name: string }; to: { userId: string; name: string }; expectedVersion: number; now: string; clientId?: string; toAssignedToClient?: boolean }): { origin: Todo; copy: Todo } {
  checkVersion(t, i.expectedVersion);
  if (t.status !== 'OPEN') fail('CONFLICT', '끝난 할 일은 맡길 수 없습니다.');
  if (t.delegatedTo || t.delegatedFrom) fail('CONFLICT', '이미 맡겼거나 맡겨 받은 일입니다.');
  if (i.to.userId === t.ownerId) fail('VALIDATION', '다른 선생님을 고르세요.');
  // 아동과 연결된 일은 그 아동의 담당 선생님에게만(담당이 아닌 선생님에게 아동 정보가 넘어가지 않게)
  if (i.clientId && !i.toAssignedToClient) fail('FORBIDDEN', '이 아동의 담당 선생님에게만 맡길 수 있습니다.');
  const copy = newTodo({
    id: i.copyId, ownerId: i.to.userId, title: t.title, notes: t.notes, deadline: t.deadline, now: i.now,
    checklist: t.checklist.map((c) => ({ ...c, done: false })), delegatedFrom: { userId: i.from.userId, name: i.from.name, todoId: t.id },
  });
  if (i.clientId) copy.clientId = i.clientId;
  const origin = normalized({
    ...t, ...(i.clientId ? { clientId: i.clientId } : {}), bucket: 'WAITING', waitingFor: i.to.name, startDate: undefined, evening: false,
    delegatedTo: { userId: i.to.userId, name: i.to.name, todoId: copy.id }, updatedAt: i.now, version: t.version + 1,
  });
  return { origin, copy };
}

/** 받는 사람이 끝내거나 다시 열면 원본에는 끝났는지만 반영한다. 원본은 맡긴 사람이 확인하고 닫는다. */
export function reportDelegated(origin: Todo, doneAt: string | undefined, now: string): Todo {
  const { doneAt: _old, ...link } = origin.delegatedTo ?? fail('CONFLICT', '맡긴 일이 아닙니다.');
  // 맡긴 사람이 이미 확인하고 닫았으면 사본을 다시 열 수 없다(열어도 맡긴 사람에게 보이지 않는다)
  if (origin.status !== 'OPEN') fail('CONFLICT', '맡긴 분이 이미 마무리한 일이라 다시 열 수 없습니다.');
  return { ...origin, delegatedTo: { ...link, ...(doneAt ? { doneAt } : {}) }, updatedAt: now, version: origin.version + 1 };
}

/** 받은 사람이 탈퇴해 사본이 없어지면, 열린 원본을 맡긴 사람의 수신함으로 돌려 다시 정하게 한다. */
export function returnDelegated(origin: Todo, now: string): Todo {
  const { delegatedTo, ...rest } = origin;
  const link = delegatedTo ?? fail('CONFLICT', '맡긴 일이 아닙니다.');
  const note = `(${link.name}님이 서비스를 떠나 돌아온 일입니다.)`;
  const notes = rest.notes ? `${rest.notes.slice(0, 4000 - note.length - 1)}\n${note}` : note;
  return normalized({ ...rest, bucket: 'INBOX', waitingFor: undefined, notes, updatedAt: now, version: origin.version + 1 });
}

/** 맡긴 사람이 원본을 닫거나 지우면 아직 열린 사본은 취소된다. */
/**
 * 맡긴 사람이 원본을 닫거나 지우면, 받은 사람의 열린 사본은 남기되 내용(아동 정보가 섞였을 수 있음)은 비우고
 * "원장이 취소함"으로 표시한다. 받은 선생님은 일이 사라진 이유를 알 수 있고, 내용은 더 남지 않는다.
 */
export function cancelDelegatedCopy(copy: Todo, now: string): Todo {
  return {
    ...copy, title: CANCELED_BY_DELEGATOR_TITLE, notes: '', checklist: [], tags: [], deadline: undefined, startDate: undefined, clientId: undefined,
    canceledByDelegator: true, status: 'CANCELED', completedAt: now, updatedAt: now, version: copy.version + 1,
  };
}

export function newTodoProject(i: { id: string; ownerId: string; title: string; goal?: string; area?: string; deadline?: string; now: string }): TodoProject {
  return normalizedProject({ id: i.id, ownerId: i.ownerId, title: i.title, goal: i.goal ?? '', area: i.area ?? '', headings: [], status: 'OPEN', deadline: i.deadline, createdAt: i.now, updatedAt: i.now, version: 1 });
}

function normalizedProject(p: TodoProject): TodoProject {
  if (p.goal.length > 2000) fail('VALIDATION', '목표는 2000자까지입니다.');
  if (p.area.trim().length > 40) fail('VALIDATION', '영역 이름은 40자까지입니다.');
  if (p.headings.length > 30) fail('VALIDATION', '제목은 30개까지입니다.');
  checkDate(p.deadline, '마감');
  return { ...p, title: checkTitle(p.title, '프로젝트 이름', 100), area: p.area.trim(), headings: p.headings.map((h) => ({ id: h.id, title: checkTitle(h.title, '제목', 60) })) };
}

export function updateTodoProject(p: TodoProject, patch: TodoProjectPatch, expectedVersion: number, now: string): TodoProject {
  checkVersion(p, expectedVersion);
  const { deadline, ...rest } = patch;
  const next: TodoProject = { ...p, ...rest, updatedAt: now, version: p.version + 1 };
  if (deadline !== undefined) next.deadline = deadline ?? undefined;
  return normalizedProject(next);
}

/** 남은 행동이 있으면 프로젝트를 완료할 수 없다(GTD: 다음 행동이 없어야 끝난 것). */
export function setTodoProjectStatus(p: TodoProject, status: TodoProjectStatus, todos: readonly Todo[], expectedVersion: number, now: string): TodoProject {
  checkVersion(p, expectedVersion);
  if (status === 'DONE' && todos.some((t) => t.projectId === p.id && t.status === 'OPEN')) fail('CONFLICT', '남은 할 일을 먼저 끝내거나 옮겨 주세요.');
  const { completedAt: _old, ...rest } = p;
  const finished = status === 'DONE' || status === 'CANCELED';
  return { ...rest, status, updatedAt: now, version: p.version + 1, ...(finished ? { completedAt: now } : {}) };
}

/* ── 목록 계산 ─────────────────────────────────────────── */

const openProject = (t: Todo, projects: readonly TodoProject[]) => {
  const p = t.projectId ? projects.find((x) => x.id === t.projectId) : undefined;
  return !p || p.status === 'OPEN';
};
const byDate = (a?: string, b?: string) => (a ?? '9999').localeCompare(b ?? '9999');

export function todoView(view: TodoView, todos: readonly Todo[], projects: readonly TodoProject[], today: string): Todo[] {
  const open = todos.filter((t) => t.status === 'OPEN');
  const live = open.filter((t) => openProject(t, projects));
  const byCreated = (a: Todo, b: Todo) => a.createdAt.localeCompare(b.createdAt);
  switch (view) {
    case 'INBOX': return open.filter((t) => t.bucket === 'INBOX').sort(byCreated);
    case 'TODAY': return live
      .filter((t) => (t.bucket === 'ACTIVE' && t.startDate !== undefined && t.startDate <= today) || ((t.bucket === 'ACTIVE' || t.bucket === 'WAITING') && t.deadline !== undefined && t.deadline <= today))
      .sort((a, b) => byDate(a.deadline, b.deadline) || byCreated(a, b));
    case 'UPCOMING': return live
      .filter((t) => t.bucket === 'ACTIVE' && ((t.startDate ?? '') > today || (!t.startDate && (t.deadline ?? '') > today)))
      .sort((a, b) => byDate(a.startDate ?? a.deadline, b.startDate ?? b.deadline) || byCreated(a, b));
    case 'NEXT': return live.filter((t) => t.bucket === 'ACTIVE' && !((t.startDate ?? '') > today)).sort((a, b) => byDate(a.deadline, b.deadline) || byCreated(a, b));
    case 'WAITING': return open.filter((t) => t.bucket === 'WAITING').sort(byCreated);
    case 'SOMEDAY': return open.filter((t) => t.bucket === 'SOMEDAY' || (t.bucket === 'ACTIVE' && !openProject(t, projects))).sort(byCreated);
    case 'LOGBOOK': return todos.filter((t) => t.status !== 'OPEN').sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''));
  }
}

export function todoCounts(todos: readonly Todo[], projects: readonly TodoProject[], today: string): Record<TodoView, number> {
  return Object.fromEntries(TODO_VIEWS.map((v) => [v, todoView(v, todos, projects, today).length])) as Record<TodoView, number>;
}

export function projectProgress(p: TodoProject, todos: readonly Todo[]) {
  const mine = todos.filter((t) => t.projectId === p.id && t.status !== 'CANCELED');
  const done = mine.filter((t) => t.status === 'DONE').length;
  return { done, total: mine.length, ratio: mine.length ? done / mine.length : 0 };
}

/** 진행 중인데 지금 할 수 있는 행동이 없는 프로젝트(주간 검토에서 다음 행동을 정한다). */
export function stalledProjects(todos: readonly Todo[], projects: readonly TodoProject[]) {
  return projects.filter((p) => p.status === 'OPEN' && !todos.some((t) => t.projectId === p.id && t.status === 'OPEN' && t.bucket === 'ACTIVE'));
}

export const daysBetween = (from: string, to: string) => Math.round((utc(to) - utc(from)) / 86400_000);

/** 주간 검토: 머릿속과 목록을 다시 맞추기 위해 볼 것들. */
export function weeklyReview(todos: readonly Todo[], projects: readonly TodoProject[], today: string, lastReviewedAt?: string) {
  const open = todos.filter((t) => t.status === 'OPEN');
  const dateOf = (iso: string) => new Date(Date.parse(iso) + 9 * 3600_000).toISOString().slice(0, 10);
  return {
    due: !lastReviewedAt || daysBetween(dateOf(lastReviewedAt), today) >= TODO_REVIEW_DAYS,
    inbox: todoView('INBOX', todos, projects, today),
    overdue: open.filter((t) => t.bucket !== 'SOMEDAY' && t.deadline !== undefined && t.deadline < today).sort((a, b) => byDate(a.deadline, b.deadline)),
    stalled: stalledProjects(todos, projects),
    waiting: todoView('WAITING', todos, projects, today),
    staleWaiting: todoView('WAITING', todos, projects, today).filter((t) => daysBetween(dateOf(t.updatedAt), today) >= TODO_WAITING_STALE_DAYS),
    someday: todoView('SOMEDAY', todos, projects, today),
    doneThisWeek: todos.filter((t) => t.status === 'DONE' && t.completedAt && daysBetween(dateOf(t.completedAt), today) < TODO_REVIEW_DAYS).length,
  };
}

/** 목록 응답에 넣을 할 일: 열린 일과 진행 중·언젠가 프로젝트의 끝난 일은 모두, 나머지 끝난 일은 최근 것부터 TODO_LOGBOOK_LIMIT 개. */
export function todosToSend(todos: readonly Todo[], projects: readonly TodoProject[]) {
  const live = new Set(projects.filter((p) => p.status === 'OPEN' || p.status === 'SOMEDAY').map((p) => p.id));
  const keep = (t: Todo) => t.status === 'OPEN' || (t.projectId !== undefined && live.has(t.projectId));
  const older = todos.filter((t) => !keep(t)).sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''));
  return { todos: [...todos.filter(keep), ...older.slice(0, TODO_LOGBOOK_LIMIT)], olderDone: Math.max(0, older.length - TODO_LOGBOOK_LIMIT) };
}

export const todoTags = (todos: readonly Todo[]) => [...new Set(todos.flatMap((t) => t.tags))].sort((a, b) => a.localeCompare(b, 'ko'));

/** 제목·메모·태그·체크리스트·기다리는 대상에서 찾는다(끝난 것 포함). */
export function searchTodos(todos: readonly Todo[], query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return todos.filter((t) => [t.title, t.notes, t.waitingFor ?? '', ...t.tags, ...t.checklist.map((c) => c.text)].some((s) => s.toLowerCase().includes(q)));
}

/* ── 날짜 입력 도우미 ──────────────────────────────────── */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const EN_WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const addDays = (date: string, n: number) => new Date(utc(date) + n * 86400_000).toISOString().slice(0, 10);
const weekdayOf = (date: string) => new Date(utc(date)).getUTCDay();

/** 화면 표시: 오늘, 내일, 11월 9일 (월). 올해가 아니면 연도를 붙인다. */
export function todoDateLabel(date: string, today: string): string {
  const d = daysBetween(today, date);
  if (d === 0) return '오늘';
  if (d === 1) return '내일';
  if (d === -1) return '어제';
  const [y, m, day] = date.split('-').map(Number);
  return `${y === Number(today.slice(0, 4)) ? '' : `${y}년 `}${m}월 ${day}일 (${WEEKDAYS[weekdayOf(date)]})`;
}

function validDate(y: number, m: number, d: number) {
  const s = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return realDate(s) ? s : undefined;
}

/**
 * 짧은 입력을 날짜 후보로 바꾼다: 오늘·내일·모레, 월(또는 월요일·mon), 다음주 금, 3일 뒤, 2주 후, 11/20, 11월 20일, 20일, 2026-12-01.
 * 요일은 오늘을 뺀 가장 가까운 세 번을 돌려준다.
 */
export function parseTodoDate(input: string, today: string): string[] {
  const s = input.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return [];
  const words: Record<string, number> = { 오늘: 0, 내일: 1, 모레: 2, 글피: 3 };
  if (Object.hasOwn(words, s)) return [addDays(today, words[s]!)];
  const rel = /^(\d{1,3})(일|주)(뒤|후)$/.exec(s);
  if (rel) return [addDays(today, Number(rel[1]) * (rel[2] === '주' ? 7 : 1))];
  const wk = /^(다음주|담주|이번주)?([일월화수목금토])(요일)?$/.exec(s);
  const en = EN_WEEKDAYS.findIndex((w) => s.startsWith(w) && 'sunday monday tuesday wednesday thursday friday saturday'.includes(s));
  if (wk || en >= 0) {
    const day = wk ? WEEKDAYS.indexOf(wk[2]!) : en;
    const ahead = (day - weekdayOf(today) + 7) % 7 || 7;
    if (wk?.[1] === '다음주' || wk?.[1] === '담주') {
      const monday = addDays(today, ((8 - weekdayOf(today)) % 7) || 7); // 월요일 시작
      return [addDays(monday, (day + 6) % 7)];
    }
    if (wk?.[1] === '이번주') {
      // 월요일 시작인 이번 주의 그 요일. 이미 지났으면 후보가 없다.
      const date = addDays(today, (day + 6) % 7 - (weekdayOf(today) + 6) % 7);
      return date >= today ? [date] : [];
    }
    return [0, 7, 14].map((n) => addDays(today, ahead + n));
  }
  if (DATE.test(s)) { const [y, m, d] = s.split('-').map(Number); return [validDate(y!, m!, d!)].filter((x): x is string => Boolean(x)); }
  const md = /^(\d{1,2})(?:\/|월)(\d{1,2})일?$/.exec(s);
  const year = Number(today.slice(0, 4));
  const nextOf = (m: number, d: number) => {
    const here = validDate(year, m, d);
    return here && here >= today ? here : validDate(year + 1, m, d);
  };
  if (md) return [nextOf(Number(md[1]), Number(md[2]))].filter((x): x is string => Boolean(x));
  const dayOnly = /^(\d{1,2})일$/.exec(s);
  if (dayOnly) {
    const month = Number(today.slice(5, 7));
    const here = validDate(year, month, Number(dayOnly[1]));
    const next = month === 12 ? validDate(year + 1, 1, Number(dayOnly[1])) : validDate(year, month + 1, Number(dayOnly[1]));
    return [here && here >= today ? here : next].filter((x): x is string => Boolean(x));
  }
  return [];
}
